import {useState, useCallback, useEffect, useMemo} from 'react';
import produce from 'immer';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import {setProperty, getErrorCode} from '../../../utils/utils';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes, ErrorCodes} from '../../../api/consts';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {SystemSettingsGetSchema} from '../../../api/DBSchema/SystemSettings';
import generalIcon from '../../../assets/images/general.png';
import pageIcon from '../../../assets/images/page.png';
import clipboardIcon from '../../../assets/images/clipboard.png';
import globeIcon from '../../../assets/images/globe.png';
import mailIcon from '../../../assets/images/mail.png';
import licenseIcon from '../../../assets/images/license.png';
import backupIcon from '../../../assets/images/backup.png';
import settingsIcon from '../../../assets/images/settings.png';
import encryptionIcon from '../../../assets/images/encryption.png';
import megaphoneIcon from '../../../assets/images/megaphone.png';
import alertsIcon from '../../../assets/images/alerts.png';
import keyIcon from '../../../assets/images/administrator_access_16.png';
import padlockIcon from '../../../assets/images/padlock.png';
import passwordSecurityIcon from '../../../assets/images/lock_passcode.png';
import SystemSettingsGeneral from './InnerPages/SystemSettingsGeneral';
import SystemSettingsLogging from './InnerPages/SystemSettingsLogging';
import SystemSettingsMail from './InnerPages/SystemSettingsMail';
import SystemSettingsOverview from './InnerPages/SystemSettingsOverview';
import SystemSettingsHttpProxy from './InnerPages/SystemSettingsHttpProxy';
import SystemSettingsLicenseManagement from './InnerPages/SystemSettingsLicenseManagement';
import SystemSettingsSystemBackup from './InnerPages/SystemSettingsSystemBackup';
import SystemSettingsSystemBackupStatus from './InnerPages/SystemSettingsSystemBackupStatus';
import SystemSettingsCertificateAuthority from './InnerPages/SystemSettingsCertificateAuthority';
import SystemSettingsNotificationsGeneral from './InnerPages/SystemSettingsNotificationsGeneral';
import SystemSettingsNotificationReports from './InnerPages/SystemSettingsNotificationReports';
import SystemSettingsCustomAlerts from './InnerPages/SystemSettingsCustomAlerts';
import SystemSettingsManagementAccessControl from './InnerPages/SystemSettingsManagementAccessControl';
import SystemSettingsAdminAccessControl from './InnerPages/SystemSettingsAdminAccessControl';
import SystemSettingsTwoStepAuthentication from './InnerPages/SystemSettingsTwoStepAuthentication';
import SystemSettingsPasswordSecurity from './InnerPages/SystemSettingsPasswordSecurity';
import {helpContextPageUrl} from '../../../conf/Routes';

export function useSystemSettingsEditor(defaultChosenPageId) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, enqueueLogoutSnackbar } = useSMCSnackbar();
    const { getErrorMessage, getMessagesArrayByPages } = useSMCErrorHandler();

    const [origSystemSettingsObject, setOrigSystemSettingsObject] = useState({});
    const [systemSettingsObject, setSystemSettingsObject] = useState({});

    const [isPageDataLoaded, setIsPageDataLoaded] = useState(false);
    const [isInnerPageLoading, setIsInnerPageLoading] = useState(false);
    const [isPageLoadFailed, setIsPageLoadFailed] = useState(false);
    const [errorMessagesByPageList, setErrorMessagesByPageList] = useState(null);

    const [isObjectDirty, setIsObjectDirty] = useState(false);

    const [chosenPageId, setChosenPageId] = useState(defaultChosenPageId || 'general');

    const editorPages = useMemo(() => ([
        { id: SystemSettingsGetSchema.pages.general.pageId, title: t('SMP.GENERAL'), icon: generalIcon, pageElement: SystemSettingsGeneral, helpId: helpContextPageUrl.systemSettingsGeneral },
        { id: SystemSettingsGetSchema.pages.overview.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.OVERVIEW'), icon: pageIcon, pageElement: SystemSettingsOverview, helpId: helpContextPageUrl.systemSettingsOverview },        
        { id: SystemSettingsGetSchema.pages.logging.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.LOGGING'), icon: clipboardIcon, pageElement: SystemSettingsLogging, helpId: helpContextPageUrl.systemSettingsLogging },        
        { id: SystemSettingsGetSchema.pages.httpProxy.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.HTTP_PROXY'), icon: globeIcon, pageElement: SystemSettingsHttpProxy, helpId: helpContextPageUrl.systemSettingsHttpProxy },        
        { id: SystemSettingsGetSchema.pages.mail.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.MAIL_SETTINGS'), icon: mailIcon, pageElement: SystemSettingsMail, helpId: helpContextPageUrl.systemSettingsMail },        
        { id: SystemSettingsGetSchema.pages.licenseManagement.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.LICENSE_MANAGEMENT'), icon: licenseIcon, pageElement: SystemSettingsLicenseManagement, helpId: helpContextPageUrl.systemSettingsLicenseManagement },        
        { title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.SYSTEM_BACKUP'), icon: backupIcon, children: [
                { id: SystemSettingsGetSchema.pages.systemBackup.pageId, title: t('SMP.GENERAL'), icon: settingsIcon, pageElement: SystemSettingsSystemBackup, helpId: helpContextPageUrl.systemSettingsSystemBackup },                
                { id: SystemSettingsGetSchema.pages.systemBackupStatus.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.STATUS'), icon: pageIcon, pageElement: SystemSettingsSystemBackupStatus, helpId: helpContextPageUrl.systemSettingsSystemBackupStatus }
            ]
        },
        { id: SystemSettingsGetSchema.pages.certificateAuthority.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.CERT_AUTH'), icon: encryptionIcon, pageElement: SystemSettingsCertificateAuthority, helpId: helpContextPageUrl.systemSettingsCertificateAuthority },
        { title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.NOTIFICATIONS'), icon: megaphoneIcon, children: [
                { id: SystemSettingsGetSchema.pages.notifications.pageId, title: t('SMP.GENERAL'), icon: settingsIcon, pageElement: SystemSettingsNotificationsGeneral, helpId: helpContextPageUrl.systemSettingsNotificationsGeneral },
                { id: SystemSettingsGetSchema.pages.notificationReports.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.REPORTS'), icon: pageIcon, pageElement: SystemSettingsNotificationReports, helpId: helpContextPageUrl.systemSettingsNotificationsReports }
            ]
        },
        { id: SystemSettingsGetSchema.pages.customAlerts.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.CUSTOM_ALERTS'), icon: alertsIcon, pageElement: SystemSettingsCustomAlerts, helpId: helpContextPageUrl.systemSettingsCustomAlerts },
        { title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.ACCESS_CONTROL'), icon: keyIcon, children: [
                { id: SystemSettingsGetSchema.pages.managementAccessControl.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.MANAGEMENT_ACCESS_CONTROL'), icon: keyIcon, pageElement: SystemSettingsManagementAccessControl, helpId: helpContextPageUrl.systemSettingsManagementAccessControl },
                { id: SystemSettingsGetSchema.pages.adminAccessControl.pageId, title: t('SMP.SYSTEM.SETTINGS.INNER_MENU.ADMIN_ACCESS_CONTROL'), icon: keyIcon, pageElement: SystemSettingsAdminAccessControl, helpId: helpContextPageUrl.systemSettingsAdminAccessControl }
            ]
        },
        { id: SystemSettingsGetSchema.pages.twoStepAuth.pageId, title: t('SMP.PAGE.2FA'), icon: padlockIcon, pageElement: SystemSettingsTwoStepAuthentication, helpId: helpContextPageUrl.settingsTwoStepAuthentication },
        { id: SystemSettingsGetSchema.pages.userPasswordSecurity.pageId, title: 'System Password Security', icon: passwordSecurityIcon, pageElement: SystemSettingsPasswordSecurity }
    ]), [t]);

    const [pageIdToPageName, pageIdToHelpId] = useMemo(() => {
        const _pageIdToPageName = {};
        const _pageIdToHelpId = {};
        const addPagesData = (pagesRoot) => {
            pagesRoot.forEach(page => {
                if (page.id) {
                    _pageIdToPageName[page.id] = page.title;
                    _pageIdToHelpId[page.id] = page.helpId;
                }
                if (page.children) {
                    addPagesData(page.children);
                }
            })
        }
        addPagesData(editorPages);
        return [_pageIdToPageName, _pageIdToHelpId];
    }, [editorPages]);

    const helpId = useMemo(() => pageIdToHelpId[chosenPageId], [pageIdToHelpId, chosenPageId]);

    const fetchSystemSettings = useCallback(async () => {
        setIsPageDataLoaded(false);
        try {
            const fetchSystemSettingsResp = await Api.fetchSingleObject(ObjectTypes.SYSTEM_SETTINGS);
            const systemSettings = fetchSystemSettingsResp.data.data;
            // Validate server response
            if (!systemSettings) {
                console.error('Get system settings response failed: system settings response returned empty');
                throw new Error(ErrorCodes.SERVER_ERROR);
            }
            setOrigSystemSettingsObject(systemSettings);
            setSystemSettingsObject(systemSettings);
            setIsPageLoadFailed(false);
            setIsObjectDirty(false); // Just fetched, not dirty
        } catch (e) {
            const errorCode = getErrorCode(e) || ApiErrorCodes.GENERAL_ERROR;
            if (errorCode === ApiErrorCodes.INVALID_SESSION_ERROR) {
                enqueueLogoutSnackbar(t(errorCode));
            } else {
                enqueueErrorSnackbar(t(errorCode));
            }
            setIsPageLoadFailed(true);
        }
        setIsPageDataLoaded(true);
    }, [enqueueErrorSnackbar, enqueueLogoutSnackbar, t]);
    
    const setSystemSettingsData = useCallback((paramPath, newValue) => {
        setIsObjectDirty(true); // Data changed, object is dirty
        setSystemSettingsObject(systemSettingsObj => produce(systemSettingsObj, draft => {
            setProperty(draft, paramPath, newValue);
        }));
    }, []);

    const onPageChange = useCallback((pageId) => {
        setIsInnerPageLoading(true);
        setChosenPageId(pageId);
        Api.verifySingleObject(ObjectTypes.SYSTEM_SETTINGS, undefined, systemSettingsObject)
        .then(() => {
            // On no error, clear error message
            setErrorMessagesByPageList(null);
        })
        .catch((error) => {
            const errorMessageArrayByPage = getMessagesArrayByPages(error, pageIdToPageName);
            if (errorMessageArrayByPage) {
                setErrorMessagesByPageList(errorMessageArrayByPage);
            }
        })
        .finally(() => {
            setIsInnerPageLoading(false);
        });
    }, [getMessagesArrayByPages, systemSettingsObject, pageIdToPageName]);


    const saveButtonOnClick = useCallback(() => {
        Api.updateSingleObject(ObjectTypes.SYSTEM_SETTINGS, undefined, systemSettingsObject)
        .then(() => {
            setIsPageDataLoaded(false);
            setErrorMessagesByPageList(null);
            fetchSystemSettings();
            enqueueSuccessSnackbar(t('OBJECT_EDITOR_OBJ_SAVED_OK'));
        })
        .catch((error) => {
            const errorMessage = getErrorMessage(error);
            alert(errorMessage);
        })
        .finally(() => {
            setIsPageDataLoaded(true);
        });
    }, [fetchSystemSettings, getErrorMessage, systemSettingsObject, enqueueSuccessSnackbar, t]);

    const revertButtonOnClick = useCallback(() => {
        setSystemSettingsObject(origSystemSettingsObject);
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
        setErrorMessagesByPageList(null);
    }, [origSystemSettingsObject]);

    const refreshAction = useCallback(() => {
        fetchSystemSettings();
        setIsObjectDirty(false); // Is not dirty, object is reset to originl values
    }, [fetchSystemSettings]);

    const systemBackupRunNowAction = useCallback(() => {
        if (window.confirm(t('EXT_UI.SMP.SYSTEM_BACKUP.RUN_NOW_ALERT1')+'\n'+t('EXT_UI.SMP.SYSTEM_BACKUP.RUN_NOW_ALERT2'))) {
            Api.doAction(ObjectTypes.SYSTEM_SETTINGS, 'system-backup-run-now')
            .then(resp => {
                enqueueSuccessSnackbar(t('EXT_UI.SMP.SYSTEM_BACKUP.RUN_NOW_ALERT_SUCCESS'));
            })
            .catch(e => {
                const errorMessage = getErrorMessage(e);
                enqueueErrorSnackbar(errorMessage);
            })
        }
    }, [enqueueErrorSnackbar, enqueueSuccessSnackbar, getErrorMessage, t]);

    // Fetch system settings on loading of page
    useEffect(() => {
        fetchSystemSettings();
    }, [fetchSystemSettings]);
    
    return {
        systemSettingsObject,
        isObjectDirty,
        editorPages,
        helpId,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setSystemSettingsData,
        saveButtonOnClick,
        revertButtonOnClick,
        reloadPageData: fetchSystemSettings,
        refreshAction,
        systemBackupRunNowAction
    };
}